<script setup lang="ts">
import { ref, onMounted, computed, nextTick, provide } from 'vue';
import { useTrip } from '@/stores/trip';
import { useDrawer } from '@/composables/useDrawer';
import { useToast } from '@/composables/useToast';

import AppSidebar from './AppSidebar.vue';
import MapArea from './MapArea.vue';
import OnboardingModal from './OnboardingModal.vue';

/**
 * Racine de l'application EuroRail.
 *
 * Joue le rôle de "fake body" de l'app : c'est l'élément qui :
 *   - Limite la hauteur à `100dvh - var(--site-header-height)` (via CSS)
 *   - Sert d'ancre au drawer mobile (position absolute du drawer relatif à ce wrap)
 *   - Coordonne l'interaction map ↔ drawer (replier le drawer au clic carte, etc.)
 *
 * NOTE : porte la classe `app-embed-root` et l'attribut `data-drawer-state`.
 * Le header de la page (branding + lien retour) vit dans `App.vue`.
 */
const trip = useTrip();
const { show: showToast } = useToast();

const containerRef = ref<HTMLElement | null>(null);
const mapRef = ref<InstanceType<typeof MapArea> | null>(null);
const sidebarRef = ref<InstanceType<typeof AppSidebar> | null>(null);
const onboardingRef = ref<InstanceType<typeof OnboardingModal> | null>(null);

// Récup des refs internes au mount
const sidebarEl = ref<HTMLElement | null>(null);
const handleEl = ref<HTMLElement | null>(null);

onMounted(async () => {
  await nextTick();
  // Le composant Sidebar expose ses refs via defineExpose
  sidebarEl.value = sidebarRef.value?.sidebarRef ?? null;
  handleEl.value = sidebarRef.value?.handleRef ?? null;
});

// On expose une fonction "ouvrir l'aide" via provide() pour que la topbar
// (et n'importe quel composant descendant) puisse la déclencher sans prop drilling.
provide('openHelp', () => onboardingRef.value?.forceOpen());

// ---------- Drawer mobile ----------

const { drawerState, setDrawerState, isMobile } = useDrawer({
  containerRef,
  sidebarRef: sidebarEl,
  handleRef: handleEl,
  onStateChange: () => mapRef.value?.invalidateSize(),
});

// ---------- Coordination map / drawer ----------

function onPopupOpen() {
  // Quand un marqueur ouvre un popup, on rétracte le drawer pour voir la carte
  if (isMobile() && drawerState.value === 'full') {
    setDrawerState('peek');
  }
}

function onMapClick() {
  // Clic sur la carte (hors marqueur) : retour à peek
  if (isMobile() && drawerState.value !== 'peek') {
    setDrawerState('peek');
  }
}

function onFabClick() {
  setDrawerState('mid');
}

const showFab = computed(() => {
  return isMobile() && drawerState.value === 'peek';
});

// ---------- Toast initial si chargé depuis URL ----------

onMounted(() => {
  if (trip.urlLoaded && trip.state.routeStops.length > 0) {
    setTimeout(() => showToast('Itinéraire chargé depuis le lien', 'info'), 500);
  }
});
</script>

<template>
  <div
    ref="containerRef"
    class="app-embed-root relative flex flex-col w-full mx-auto max-w-app bg-bg overflow-hidden"
  >
    <main class="flex-1 min-h-0 grid md:grid-cols-[var(--sidebar-width)_1fr] md:gap-4 md:p-4 relative">
      <AppSidebar ref="sidebarRef" />
      <MapArea ref="mapRef" @popup-open="onPopupOpen" @map-click="onMapClick" />
    </main>

    <!-- FAB mobile : visible quand drawer = peek -->
    <Transition
      enter-active-class="transition duration-200"
      leave-active-class="transition duration-200"
      enter-from-class="opacity-0 translate-y-2"
      leave-to-class="opacity-0 translate-y-2"
    >
      <button
        v-if="showFab"
        type="button"
        class="md:hidden absolute bottom-32 right-4 z-[1400] inline-flex items-center gap-2 px-4 py-3 bg-accent text-white rounded-full shadow-lg hover:bg-accent-hover transition-colors duration-160"
        aria-label="Ouvrir le panneau"
        @click="onFabClick"
      >
        <i class="ti ti-adjustments-alt" aria-hidden="true"></i>
        <span class="text-sm font-medium">Itinéraire</span>
      </button>
    </Transition>

    <OnboardingModal ref="onboardingRef" />
  </div>
</template>
